import { stringify } from 'qs';
import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { FossabotPRCountsResponse } from '../../../../shared/fossabot/response';

function fetchFossabotPRCounts(projectLocator: string) {
  /* qs encodes by default, which project locators need: they contain slashes and '+'. */
  const queryString = stringify({ projectLocator }, { addQueryPrefix: true });
  return get<FossabotPRCountsResponse>(`/api/fossabot/dependency-upgrade-prs/counts${queryString}`);
}

/**
 * Prefix shared by every fossabot PR query (the counts and the list, for every
 * project); invalidating it refetches them all.
 */
export const getFossabotPRsQueryKeyPrefix = (): QueryKey => ['fossabot', 'dependency-upgrade-prs'];

export const getFossabotPRCountsQueryKey = (projectLocator: string): QueryKey =>
  [...getFossabotPRsQueryKeyPrefix(), projectLocator, 'counts'];

/**
 * Aggregate fossabot dependency-upgrade PR counts for a project, used by the
 * stat cards, the subnav badge and the filter chips.
 *
 * The analysis counts (analyzed, needsReview) are a separate axis from the VCS
 * state counts (open, draft, closed, merged), so they overlap with them and must
 * never be summed into `total`.
 */
export function useFossabotPRCounts(projectLocator: string, shouldFetch = true) {
  return useQuery<FossabotPRCountsResponse>({
    queryKey: getFossabotPRCountsQueryKey(projectLocator),
    queryFn: () => fetchFossabotPRCounts(projectLocator),
    enabled: shouldFetch && !!projectLocator,
  });
}
